import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement Lygos
export interface LygosElearningPaymentDetails {
  amount: number; // Montant en FCFA
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  return_base_url?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface LygosElearningPaymentResponse {
  order: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string;
    payment_url: string;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface LygosElearningStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'paid' | 'completed';
  data: any;
}

/**
 * Initie un paiement Lygos via la fonction Edge spécifique pour l'e-learning
 */
export const initiateLygosElearningEdgePayment = async (
  details: LygosElearningPaymentDetails
): Promise<LygosElearningPaymentResponse> => {
  try {
    console.log('[LygosElearningEdge] Initiation du paiement Lygos:', details);

    // Utiliser la fonction edge spécifique pour l'e-learning
    const response = await fetch(`${config.supabaseUrl}/functions/v1/elearning-lygos-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[LygosElearningEdge] Erreur de la fonction edge:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement Lygos");
    }

    const data = await response.json();
    console.log('[LygosElearningEdge] Paiement Lygos initié avec succès:', data);
    return data;
  } catch (error) {
    console.error("[LygosElearningEdge] Erreur lors de l'initiation du paiement:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement Lygos via la base de données pour l'e-learning
 */
export const checkLygosElearningEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<LygosElearningStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[LygosElearningEdge][${requestId}] Vérification du statut - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[LygosElearningEdge][${requestId}] Vérification du statut dans la base de données`);
    
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[LygosElearningEdge][${requestId}] Requête terminée en ${queryTime}ms`);
    console.log(`[LygosElearningEdge][${requestId}] Résultat:`, { 
      order: order ? {
        status: order.status,
        payment_reference: order.payment_reference,
        created_at: order.created_at
      } : null, 
      error: error ? error.message : null 
    });
    
    if (error) {
      console.error(`[LygosElearningEdge][${requestId}] Erreur lors de la récupération:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[LygosElearningEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[LygosElearningEdge][${requestId}] Statut de la commande: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'completed') {
      console.log(`[LygosElearningEdge][${requestId}] Commande déjà payée, retour immédiat`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'completed',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[LygosElearningEdge][${requestId}] Commande en attente (statut: ${order.status})`);
    
    // Retourner le statut actuel
    return {
      status: order.status === 'pending_payment' ? 'pending' : 'failed',
      data: {
        order_id,
        payment_reference: reference,
        status: order.status,
        created_at: order.created_at
      }
    };
  } catch (error) {
    console.error(`[LygosElearningEdge][${requestId}] Erreur lors de la vérification:`, error);
    throw error;
  }
};
